/**
 * PDF Preview Component
 *
 * Renders PDF thumbnail and provides full viewer modal.
 * Uses pdfjs-dist for rendering.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './PdfPreview.css';

// Set up the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

interface PdfPreviewProps {
  /** PDF data as Uint8Array or URL */
  data: Uint8Array | string;
  /** Optional filename for display */
  filename?: string;
  /** Max width for thumbnail (default: 200) */
  thumbnailWidth?: number;
  /** Whether to show full viewer on click (default: true) */
  clickToView?: boolean;
  /** Optional class name */
  className?: string;
}

interface PdfState {
  status: 'loading' | 'ready' | 'error';
  numPages: number;
  currentPage: number;
  error?: string;
}

export function PdfPreview({
  data,
  filename,
  thumbnailWidth = 200,
  clickToView = true,
  className = '',
}: PdfPreviewProps) {
  const [state, setState] = useState<PdfState>({
    status: 'loading',
    numPages: 0,
    currentPage: 1,
  });
  const [showViewer, setShowViewer] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const viewerCanvasRef = useRef<HTMLCanvasElement>(null);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        setState(s => ({ ...s, status: 'loading' }));

        // Load the PDF
        const loadingTask = typeof data === 'string'
          ? pdfjsLib.getDocument(data)
          : pdfjsLib.getDocument({ data });

        const pdf = await loadingTask.promise;

        if (cancelled) {
          pdf.destroy();
          return;
        }

        pdfDocRef.current = pdf;

        setState({
          status: 'ready',
          numPages: pdf.numPages,
          currentPage: 1,
        });

        // Render thumbnail (first page)
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const scale = thumbnailWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const context = canvas.getContext('2d');
        if (context) {
          await page.render({
            canvasContext: context,
            viewport: scaledViewport,
          }).promise;

          if (!cancelled) {
            setThumbnailUrl(canvas.toDataURL());
          }
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            numPages: 0,
            currentPage: 1,
            error: err instanceof Error ? err.message : 'Failed to load PDF',
          });
        }
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [data, thumbnailWidth]);

  // Render current page in viewer
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDocRef.current || !viewerCanvasRef.current) return;

    const page = await pdfDocRef.current.getPage(pageNum);
    const canvas = viewerCanvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Calculate scale to fit in viewport (max 800px width)
    const viewport = page.getViewport({ scale: 1 });
    const maxWidth = Math.min(800, window.innerWidth - 80);
    const scale = maxWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;

    await page.render({
      canvasContext: context,
      viewport: scaledViewport,
    }).promise;
  }, []);

  // Render page when viewer opens or page changes
  useEffect(() => {
    if (showViewer && state.status === 'ready') {
      renderPage(state.currentPage);
    }
  }, [showViewer, state.currentPage, state.status, renderPage]);

  // Navigation handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= state.numPages) {
      setState(s => ({ ...s, currentPage: page }));
    }
  };

  const prevPage = () => goToPage(state.currentPage - 1);
  const nextPage = () => goToPage(state.currentPage + 1);

  // Handle thumbnail click
  const handleThumbnailClick = () => {
    if (clickToView && state.status === 'ready') {
      setShowViewer(true);
    }
  };

  // Close viewer
  const closeViewer = () => {
    setShowViewer(false);
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!showViewer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          prevPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          nextPage();
          break;
        case 'Escape':
          e.preventDefault();
          closeViewer();
          break;
        case 'Home':
          e.preventDefault();
          goToPage(1);
          break;
        case 'End':
          e.preventDefault();
          goToPage(state.numPages);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showViewer, state.numPages, state.currentPage]);

  return (
    <>
      {/* Thumbnail */}
      <div
        className={`pdf-preview ${className} ${state.status} ${clickToView ? 'clickable' : ''}`}
        onClick={handleThumbnailClick}
      >
        {state.status === 'loading' && (
          <div className="pdf-loading">
            <div className="pdf-spinner" />
            <span>Loading PDF...</span>
          </div>
        )}

        {state.status === 'error' && (
          <div className="pdf-error">
            <span className="pdf-error-icon">!</span>
            <span>{state.error}</span>
          </div>
        )}

        {state.status === 'ready' && thumbnailUrl && (
          <>
            <img src={thumbnailUrl} alt={filename || 'PDF preview'} className="pdf-thumbnail" />
            <div className="pdf-info">
              {filename && <span className="pdf-filename">{filename}</span>}
              <span className="pdf-pages">{state.numPages} page{state.numPages !== 1 ? 's' : ''}</span>
            </div>
            {clickToView && (
              <div className="pdf-overlay">
                <span>Click to view</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Full Viewer Modal */}
      {showViewer && (
        <div className="pdf-viewer-overlay" onClick={closeViewer}>
          <div className="pdf-viewer" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="pdf-viewer-header">
              <span className="pdf-viewer-title">{filename || 'PDF Document'}</span>
              <button className="pdf-viewer-close" onClick={closeViewer}>
                &times;
              </button>
            </div>

            {/* Canvas */}
            <div className="pdf-viewer-content">
              <canvas ref={viewerCanvasRef} className="pdf-viewer-canvas" />
            </div>

            {/* Navigation */}
            <div className="pdf-viewer-nav">
              <button
                className="pdf-nav-btn"
                onClick={prevPage}
                disabled={state.currentPage <= 1}
              >
                &larr; Prev
              </button>
              <span className="pdf-page-info">
                Page {state.currentPage} of {state.numPages}
              </span>
              <button
                className="pdf-nav-btn"
                onClick={nextPage}
                disabled={state.currentPage >= state.numPages}
              >
                Next &rarr;
              </button>
            </div>

            {/* Keyboard hint */}
            <div className="pdf-viewer-hint">
              Use arrow keys to navigate, Escape to close
            </div>
          </div>
        </div>
      )}
    </>
  );
}
